/**
 * React hook managing the WebSocket lifecycle for virtual-web.
 *
 * On lifecycle events from virtual-core realtime:
 *  - VS_EVENT_RESULTS   → inject results into cache + invalidate (event closed, VOD ready)
 *  - VS_EVENT_CANCELLED → mark event cancelled in scheduleUpcoming cache + invalidate ongoing, toast if user has bets
 *  - VS_USER_WIN        → toast win notification
 */
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { connectWebRealtime, type RealtimeConnectionHandle, type WebRealtimeMessage } from './realtimeClient';
import { eventsQueryKeys } from 'features/events/queries';
import { videoQueryKeys } from 'features/video/queries';
import { getEventIdForDisplay } from 'features/events/eventId';
import { useTicketSlipStore } from 'features/tickets/store';
import { setBase as setServerTimeBase } from 'shared/queries/serverTime';
import Emitter from 'shared/events/emitter';
import type { ScheduleUpcomingResponse, ScheduleUpcomingFootball3d } from 'features/events/api';

export function useRealtimeConnection(
  scheduleIds: string[],
  sessionId: string | null
): { isConnected: boolean } {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = React.useState(false);
  const handleRef = React.useRef<RealtimeConnectionHandle | null>(null);

  const stableScheduleIdsKey = React.useMemo(
    () => [...scheduleIds].sort().join(','),
    [scheduleIds]
  );

  const stableScheduleIds = React.useMemo(
    () => [...scheduleIds].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableScheduleIdsKey]
  );

  React.useEffect(() => {
    if (stableScheduleIds.length === 0) return;

    const handle = connectWebRealtime(
      {
        onConnected: () => setIsConnected(true),
        onDisconnected: () => setIsConnected(false),
        onMessage: (msg: WebRealtimeMessage) => {
          switch (msg.type) {
            case 'VS_EVENT_RESULTS': {
              const sid = msg.schedule_id;
              if (sid) {
                if (sessionId != null && sessionId !== '') {
                  queryClient.invalidateQueries({ queryKey: videoQueryKeys.ongoingEvent(sid) });
                }

                // Update (don't invalidate) the scheduleUpcoming cache so events
                // get end_timestamp/duration. Invalidating would drop finished
                // events from the EventSelector; updating preserves them while
                // giving the phase system the lifecycle data it needs.
                const upcomingKey = eventsQueryKeys.scheduleUpcoming(sid);

                if (msg.scope === 'week') {
                  const weekResults = msg.payload;
                  if (weekResults && typeof weekResults === 'object') {
                    queryClient.setQueryData<ScheduleUpcomingResponse>(upcomingKey, (prev) => {
                      if (!prev || !('weeks' in prev)) return prev;
                      const prevWeeks = (prev as ScheduleUpcomingFootball3d).weeks;
                      let changed = false;
                      const updatedWeeks = prevWeeks.map((week) => {
                        // Optional: week.events may be absent from payload
                        const weekEvents = week.events ?? [];
                        const updatedEvents = weekEvents.map((e: any) => {
                          const eid = getEventIdForDisplay(e);
                          const result = weekResults[eid];
                          if (!result) return e;
                          const endTs = result.end_timestamp;
                          const dur = result.duration;
                          if (endTs == null && dur == null) return e;
                          changed = true;
                          return {
                            ...e,
                            ...(endTs != null ? { end_timestamp: Number(endTs) } : {}),
                            ...(dur != null ? { duration: Number(dur) } : {}),
                          };
                        });
                        return updatedEvents !== week.events ? { ...week, events: updatedEvents } : week;
                      });
                      return changed ? { ...prev, weeks: updatedWeeks } : prev;
                    });
                  }
                } else {
                  // Racing/baseball: payload has event_id, end_timestamp, duration
                  const eventId = msg.payload?.event_id;
                  const endTs = msg.payload?.end_timestamp;
                  const dur = msg.payload?.duration;
                  if (eventId != null && (endTs != null || dur != null)) {
                    const eid = String(eventId);
                    queryClient.setQueryData<ScheduleUpcomingResponse>(upcomingKey, (prev) => {
                      if (!prev || !('events' in prev)) return prev;
                      let changed = false;
                      const updated = prev.events.map((e: any) => {
                        if (getEventIdForDisplay(e) !== eid) return e;
                        changed = true;
                        return {
                          ...e,
                          ...(endTs != null ? { end_timestamp: Number(endTs) } : {}),
                          ...(dur != null ? { duration: Number(dur) } : {}),
                        };
                      });
                      return changed ? { ...prev, events: updated } : prev;
                    });
                  }
                }

                const eventId = msg.payload?.event_id;
                if (eventId != null) {
                  queryClient.invalidateQueries({ queryKey: eventsQueryKeys.scheduleEventResults(sid, eventId) });
                }
                queryClient.invalidateQueries({ queryKey: eventsQueryKeys.scheduleResults(sid) });
                if (msg.scope === 'week') {
                  queryClient.invalidateQueries({ queryKey: eventsQueryKeys.football3dScheduleResults(sid) });
                }
              }
              break;
            }

            case 'VS_EVENT_CANCELLED': {
              const sid = msg.schedule_id ?? msg.payload?.schedule_id;
              if (sid) {
                if (sessionId != null && sessionId !== '') {
                  queryClient.invalidateQueries({ queryKey: videoQueryKeys.ongoingEvent(sid) });
                }

                const cancelledEventId = msg.payload?.event_id;
                if (cancelledEventId != null) {
                  const eid = String(cancelledEventId);
                  const upcomingKey = eventsQueryKeys.scheduleUpcoming(sid);
                  queryClient.setQueryData<ScheduleUpcomingResponse>(upcomingKey, (prev) => {
                    if (!prev) return prev;
                    if ('weeks' in prev) {
                      const prevWeeks = (prev as ScheduleUpcomingFootball3d).weeks;
                      let anyChanged = false;
                      const updatedWeeks = prevWeeks.map((week) => {
                        let weekChanged = false;
                        // Optional: week.events may be absent from payload
                        const updatedEvents = (week.events ?? []).map((e: any) => {
                          if (getEventIdForDisplay(e) !== eid) return e;
                          weekChanged = true;
                          return { ...e, cancelled: true };
                        });
                        if (!weekChanged) return week;
                        anyChanged = true;
                        return { ...week, events: updatedEvents };
                      });
                      return anyChanged ? { ...prev, weeks: updatedWeeks } : prev;
                    }
                    if ('events' in prev) {
                      let changed = false;
                      const updatedEvents = prev.events.map((e: any) => {
                        if (getEventIdForDisplay(e) !== eid) return e;
                        changed = true;
                        return { ...e, cancelled: true };
                      });
                      return changed ? { ...prev, events: updatedEvents } : prev;
                    }
                    return prev;
                  });

                  const tips = useTicketSlipStore.getState().ticket_slip;
                  const hasBetOnEvent = tips.some(
                    (t) => String(t.event_id) === String(cancelledEventId)
                  );
                  if (hasBetOnEvent) {
                    Emitter.emit('TOAST.SHOW', {
                      message: i18next.t('EVENT_CANCELLED_ERROR'),
                      variant: 'warning',
                      durationMs: 10000,
                    });
                  }
                }
              }
              break;
            }

            case 'VS_USER_WIN': {
              const winPayload = msg.payload;
              if (winPayload) {
                const amount = winPayload.win_amount;
                // Optional display: currency may be absent from payload
                const currency = winPayload.currency ?? '';
                Emitter.emit('TOAST.SHOW', {
                  message: i18next.t('USER_WIN_MESSAGE', { amount, currency }),
                  variant: 'success',
                  durationMs: 8000,
                });
                const newBalance = winPayload.new_balance;
                if (typeof newBalance === 'number' && Number.isFinite(newBalance)) {
                  Emitter.emit('VS.USER_WIN_BALANCE', { new_balance: newBalance });
                }
              }
              break;
            }

            case 'VS_REJOIN_ACK': {
              const ts = (msg as { server_timestamp?: number }).server_timestamp;
              if (typeof ts === 'number' && Number.isFinite(ts) && ts > 0) {
                setServerTimeBase(ts < 1_000_000_000_000 ? ts * 1000 : ts);
              }
              break;
            }

            default:
              break;
          }
        },
      },
      { scheduleIds: stableScheduleIds, sessionId }
    );

    handleRef.current = handle;

    return () => {
      handle.close();
      handleRef.current = null;
      setIsConnected(false);
    };
  }, [stableScheduleIds, stableScheduleIdsKey, sessionId, queryClient]);

  return { isConnected };
}
